import { PdfLine } from "./pdfReader";
import {
  PatientDoorReportData,
  PatientInfoReportData,
} from "./patientVisitData";

const REPORT_TITLE_INDEX = 1;
const REPORT_DATA_INDEX = 3;
const REPORT_TITLE_MAGIC_STRING = "דוח תחקור";

enum TABLE_ROW_INDICES {
  DATE,
  EMAIL,
  PHONE,
  AGE,
  GENDER,
  FIRST_NAME,
  LAST_NAME,
  ID,
}
export const mergeAllCells: (line: PdfLine) => string = (line: PdfLine) => {
  if (!line) return "";
  if (typeof line === "string") return line as string;
  return line.reduce(
    (acc: string, current) => acc + mergeAllCells(current),
    "",
  );
};

export const isMaccabiPatientReport = (lines: PdfLine[]) => {
  const titleString =
    lines?.[REPORT_TITLE_INDEX] && mergeAllCells(lines?.[REPORT_TITLE_INDEX]);

  return titleString?.includes(REPORT_TITLE_MAGIC_STRING) || false;
};

// Make sure all values have 7 entries (even if some fields are missing)
const adjustIndies = (current: PdfLine) => {
  // if we have 7 index all fine
  if (current.length === TABLE_ROW_INDICES.ID + 1) return current;
  const firstFiled = mergeAllCells(current[0]);
  const secondFiled = mergeAllCells(current.length > 1 ? current[1] : "");
  // now we need to investigate:
  if (firstFiled.includes("/")) {
    // that's a date - so first index is actually right, apparently some other cells
    // are missing - continue exploring, and remove the first empty cell
    return [current[0], ...adjustIndies(current.slice(1)).slice(1)];
  }
  if (firstFiled.includes("@")) {
    // thats an email - this should be the second cell, put one cell before and make sure the rest are full
    return adjustIndies(["/", current[0], ...current.slice(1)]);
  }
  if (firstFiled.includes("-")) {
    // thats a phone number, should actually be the 3rd cell
    return adjustIndies(["/", "@", current[0], ...current.slice(1)]);
  }
  if (firstFiled.includes(".")) {
    // thats age
    return adjustIndies(["/", "@", "-", current[0], ...current.slice(1)]);
  }
  if (
    firstFiled.includes("ז") ||
    firstFiled.includes("נ") ||
    firstFiled.includes("na")
  ) {
    // thats gender
    return adjustIndies(["/", "@", "-", ".", current[0], ...current.slice(1)]);
  }
  if (!firstFiled.match("[0-9]") && !secondFiled.match("[0-9]")) {
    // thats first its not containing numbers, verry good!
    return adjustIndies([
      "/",
      "@",
      "-",
      ".",
      "na",
      current[0],
      current?.length > 1 ? current[1] : "",
      current.slice(7),
    ]);
  }
  if (firstFiled.match("[0-9]")) {
    // it has numbers, than its id (cause all the rest failed too)
    return ["/", "@", "-", ".", "na", "", "", current[0]];
  }
  return ["/", "@", "-", ".", "na", "", "", "99999"];
};

export const readMaccabiPatientReport: (
  lines: PdfLine[],
) => PatientInfoReportData[] = (lines: PdfLine[]) => {
  if (!isMaccabiPatientReport(lines))
    throw new Error(
      "Can't parse this pdf file, doesn't seem like a proper patient report",
    );
  return lines.reduce((acc, current) => {
    if (
      mergeAllCells(current[TABLE_ROW_INDICES.PHONE]).includes("טלפון") || // skip titles
      mergeAllCells(current).includes("דוח") || // skip titles
      mergeAllCells(current).trim() === "" // skip empty entries
    ) {
      return acc;
    }

    const currentRow = adjustIndies(current);

    return [
      ...acc,
      {
        email: mergeAllCells(currentRow[TABLE_ROW_INDICES.EMAIL]),
        phoneNumber: currentRow[TABLE_ROW_INDICES.PHONE],
        age: Number.parseInt(
          (currentRow[TABLE_ROW_INDICES.AGE] as string)?.split?.(".")?.[0] ??
            "",
        ),
        gender: currentRow[TABLE_ROW_INDICES.GENDER],
        firstName: mergeAllCells(currentRow[TABLE_ROW_INDICES.FIRST_NAME]),
        lastName: mergeAllCells(currentRow[TABLE_ROW_INDICES.LAST_NAME]),
        id: currentRow[TABLE_ROW_INDICES.ID],
        lastVisit: currentRow[TABLE_ROW_INDICES.DATE],
      },
    ];
  }, [] as PatientInfoReportData[]);
};
